<template>
  <div class="property-type">
    <h1 class="h4 property-type__title">
      What type of property do you live in?
    </h1>
    <RadioGroup
      v-model="selectedOption"
      class="property-type__options"
      :options="propertyTypeOptions"
    />
    <div
      v-if="isFlatSelected"
      class="property-type__flat-alert"
      data-testid="property-type-flat-alert"
      role="alert"
    >
      <i
        class="icon icon-alert property-type__flat-alert-icon"
        aria-hidden="true"
      ></i>
      <p class="property-type__flat-alert-text">
        Unfortunately solar panels aren't available for most flats yet. If you
        live in a flat where you own the roof space
        <router-link
          :to="{ name: registerInterestRoute, query: { covered: 'true' } }"
          data-testid="property-type-register-interest"
        >
          speak to our team
        </router-link>
        to see if we can help.
      </p>
    </div>
    <AppButton
      class="property-type__next-button"
      full-width
      :disabled="!selectedOption || isFlatSelected"
      size="large"
      data-testid="property-type-continue"
      @click="next"
    >
      <span>Continue</span>
      <span class="icon property-type__next-button--icon icon-arrow-right" />
    </AppButton>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import RadioGroup from "@soenergy/frontend-library/src/components/RadioButton/RadioGroup.vue"
import AppButton from "@soenergy/frontend-library/src/components/AppButton.vue"
import TrackingService from "@soenergy/frontend-library/src/services/TrackingService"
import { mapMutations } from "vuex"
import { Page } from "@/router/types"

export default defineComponent({
  components: {
    RadioGroup,
    AppButton,
  },
  emits: ["go-forward"],
  data() {
    return {
      registerInterestRoute: Page.RegisterInterest,
      propertyTypeOptions: [
        { label: "Detached", value: "detached", inputId: "input-detached" },
        {
          label: "Semi-detached",
          value: "semi-detached",
          inputId: "input-semi-detached",
        },
        { label: "Terraced", value: "terraced", inputId: "input-terraced" },
        {
          label: "Townhouse",
          value: "townhouse",
          inputId: "input-townhouse",
        },
        { label: "Bungalow", value: "bungalow", inputId: "input-bungalow" },
        { label: "Flat", value: "flat", inputId: "input-flat" },
      ],
    }
  },
  computed: {
    // committed on pick, not continue, so browser back and forward cannot drift
    // from the radio. Flat commits too; continue stays disabled for it.
    selectedOption: {
      get(): string | null {
        return this.$store.state.quote.propertyType
      },
      set(value: string | null) {
        this.setPropertyType(value)
      },
    },
    isFlatSelected(): boolean {
      return this.selectedOption === "flat"
    },
  },
  methods: {
    ...mapMutations({
      setPropertyType: "SET_PROPERTY_TYPE",
    }),
    next() {
      if (this.selectedOption && !this.isFlatSelected) {
        TrackingService.sendEvent(`Solar:PropertyType:${this.selectedOption}`)
        this.$emit("go-forward")
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.property-type {
  @include md {
    max-width: $width-7;
    margin: 0 auto;
  }

  &__title {
    text-transform: none;
    font-weight: $weight-medium;
  }

  &__options {
    margin-top: $space-5;
  }

  &__flat-alert {
    display: flex;
    align-items: flex-start;
    margin-top: $space-4;
    padding: $space-4;
    border: 1px solid $yellow-400;
    border-radius: $space-2;
    background: $yellow-50;
  }

  &__flat-alert-icon {
    flex-shrink: 0;
    margin-right: $space-3;
    font-size: $size-6;
  }

  &__flat-alert-text {
    margin: 0;
    font-size: $size-7;
    line-height: 1.5;
  }

  &__next-button {
    margin-top: $space-5;

    &--icon {
      margin-left: $space-2;
    }
  }
}
</style>
